/**
 * useCaptureUpload — Upload a Blob (camera/mic capture) via the chaos document pipeline.
 *
 * Thin wrapper around useChaosDocumentUpload.upload() that converts a Blob to a File.
 * Blob → new File([blob], filename, { type: blob.type }) → presigned S3 upload.
 *
 * Used by:
 *   LivePhotoCapture  (sourceSystem: "document-studio-photo")
 *   VoiceRecorder     (sourceSystem: "document-studio-voice")
 *
 * ARCHITECTURE:
 * - Delegates ALL upload logic to useChaosDocumentUpload (HIPAA, audit trail, S3)
 * - No direct fetch/axios calls — repository pattern only
 * - Backend generates documentId (5-layer ID enforcement)
 */

import { useCallback } from 'react';
import { useChaosDocumentUpload, type UploadResult } from './useChaosDocumentUpload';

export interface CaptureUploadOptions {
  sourceSystem?: string;
  metadata?: Record<string, unknown>;
}

export interface UseCaptureUploadReturn {
  uploadBlob: (blob: Blob, filename: string, options?: CaptureUploadOptions) => Promise<UploadResult>;
  uploadProgress: number;
  isUploading: boolean;
  error: Error | null;
}

export function useCaptureUpload(): UseCaptureUploadReturn {
  const { upload, uploadProgress, isUploading, error } = useChaosDocumentUpload();

  const uploadBlob = useCallback(
    async (blob: Blob, filename: string, options?: CaptureUploadOptions): Promise<UploadResult> => {
      // Convert Blob → File so existing upload() accepts it
      const file = new File([blob], filename, { type: blob.type });
      return upload(file, options?.sourceSystem, options?.metadata as Record<string, any>);
    },
    [upload]
  );

  // Aggregate progress across all in-flight uploads (usually just one for captures)
  const aggregateProgress = uploadProgress.length > 0
    ? uploadProgress.reduce((sum, p) => sum + p.progress, 0) / uploadProgress.length
    : 0;

  return {
    uploadBlob,
    uploadProgress: aggregateProgress,
    isUploading,
    error,
  };
}
